import React, { useState, useEffect } from 'react'
import { Globe, CheckCircle2, AlertCircle, Loader2, ExternalLink, Copy, Check, Info } from 'lucide-react'
import SectionHeading from './SectionHeading'

interface CustomDomainSettingsProps {
  tenantId: string
  userEmail?: string
  onNavigateToDeployments?: () => void
}

interface DomainConfig {
  customDomain?: string
  domainVerified: boolean
  verificationMethod: 'sso-match' | 'dns-txt' | null
  verificationToken?: string
  verifiedAt?: string
  deploymentUrl?: string
  deploymentUrls?: {
    live?: string
    preview?: string
    last?: string
  }
}

const CustomDomainSettings: React.FC<CustomDomainSettingsProps> = ({ tenantId, userEmail, onNavigateToDeployments }) => {
  const [config, setConfig] = useState<DomainConfig>({
    domainVerified: false,
    verificationMethod: null
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [previewToken, setPreviewToken] = useState<string>('')
  const [willAutoVerify, setWillAutoVerify] = useState(false)
  const [dnsStatus, setDnsStatus] = useState<{
    cname?: { configured: boolean; expected: string; actual: string | null }
    txt?: { configured: boolean; expected: string; actual: string | null } | null
  } | null>(null)
  const [checkingDns, setCheckingDns] = useState(false)

  // Get the deployment URL from the backend API (not window.location, which might be a custom domain)
  // The API returns the actual Netlify deployment URL (e.g., cloudsecurityprogram-live-123.trustcenterpage.com)
  const currentSiteUrl = config.deploymentUrl || 'trustcenter.page'

  // Debug logging
  React.useEffect(() => {
    console.log('[Custom Domain] Current site URL:', currentSiteUrl, 'Config:', config)
  }, [currentSiteUrl, config])

  useEffect(() => {
    loadConfig()
  }, [tenantId])

  // Generate preview token and check if domain will auto-verify when domain changes
  useEffect(() => {
    if (!newDomain || config.customDomain === newDomain) {
      setPreviewToken('')
      setWillAutoVerify(false)
      return
    }

    // Generate preview token (matches backend logic)
    const token = `netlify-verify-${tenantId}-preview`
    setPreviewToken(token)

    // Check if this domain will auto-verify based on email match
    if (userEmail && newDomain) {
      const emailDomain = userEmail.split('@')[1]
      const customDomainRoot = newDomain.split('.').slice(-2).join('.')
      setWillAutoVerify(emailDomain === customDomainRoot)
    } else {
      setWillAutoVerify(false)
    }
  }, [newDomain, tenantId, userEmail, config.customDomain])

  const loadConfig = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/custom-domain?tenantId=${tenantId}`, {
        credentials: 'include',
        headers
      })
      if (response.ok) {
        const data = await response.json()
        console.log('[Custom Domain] Loaded config:', data)
        setConfig(data)
        setNewDomain(data.customDomain || '')
      }
    } catch (err) {
      console.error('Failed to load domain config:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSetupDomain = async () => {
    if (!newDomain) {
      setError('Please enter a domain')
      return
    }

    setError(null)
    setSuccess(null)
    setSaving(true)

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/custom-domain?tenantId=${tenantId}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          customDomain: newDomain,
          userEmail
        })
      })

      if (response.ok) {
        const data = await response.json()
        setConfig({
          customDomain: data.customDomain,
          domainVerified: data.verified,
          verificationMethod: data.verificationMethod,
          verificationToken: data.verificationToken,
          verifiedAt: data.verified ? new Date().toISOString() : undefined
        })
        setSuccess(data.message)
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to setup domain')
      }
    } catch (err) {
      setError('Failed to setup domain')
    } finally {
      setSaving(false)
    }
  }

  const handleVerifyDomain = async () => {
    setError(null)
    setSuccess(null)
    setVerifying(true)

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/custom-domain?tenantId=${tenantId}&action=verify`, {
        method: 'PUT',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        if (data.verified) {
          setConfig(prev => ({
            ...prev,
            domainVerified: true,
            verifiedAt: new Date().toISOString()
          }))
          setSuccess(data.message)
        } else {
          // Show both message and hint if available
          const errorMsg = data.hint ? `${data.message}\n\n${data.hint}` : data.message
          setError(errorMsg)
        }
      }
    } catch (err) {
      setError('Failed to verify domain')
    } finally {
      setVerifying(false)
    }
  }

  const handleRemoveDomain = async () => {
    if (!confirm('Remove custom domain? Users will need to use the default Netlify URL.')) {
      return
    }

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const response = await fetch(`/api/custom-domain?tenantId=${tenantId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        setConfig({
          domainVerified: false,
          verificationMethod: null
        })
        setNewDomain('')
        setSuccess('Custom domain removed')
      }
    } catch (err) {
      setError('Failed to remove domain')
    }
  }

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text)
    setCopied(type)
    setTimeout(() => setCopied(null), 2000)
  }

  const checkDnsStatus = async () => {
    if (!config.customDomain) {
      console.log('[Custom Domain] Skipping DNS check - no custom domain configured')
      return
    }

    console.log('[Custom Domain] Checking DNS status for:', config.customDomain)
    setCheckingDns(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const url = `/api/custom-domain?action=check-dns`
      console.log('[Custom Domain] Fetching:', url)

      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers
      })

      console.log('[Custom Domain] DNS check response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('[Custom Domain] DNS status received:', data)
        setDnsStatus(data.dnsStatus)
      } else {
        const errorText = await response.text()
        console.error('[Custom Domain] DNS check failed:', response.status, errorText)
      }
    } catch (err) {
      console.error('[Custom Domain] Failed to check DNS status:', err)
    } finally {
      setCheckingDns(false)
    }
  }

  // Poll DNS status every 30 seconds if domain is configured
  // Keep polling even for auto-verified domains to monitor CNAME status
  useEffect(() => {
    if (config.customDomain) {
      // Only poll if CNAME is not confirmed yet
      const shouldPoll = !dnsStatus?.cname?.configured

      checkDnsStatus()

      if (shouldPoll) {
        const interval = setInterval(checkDnsStatus, 30000)
        return () => clearInterval(interval)
      }
    }
  }, [config.customDomain, tenantId, dnsStatus?.cname?.configured])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <SectionHeading id="custom-domain" className="text-lg font-semibold text-gray-900" as="h3">
          <>
            <Globe className="h-5 w-5 mr-2" />
            Custom Domain
          </>
        </SectionHeading>
        <p className="text-sm text-gray-600 mt-1">
          Use your own domain for your trust center (e.g., trust.yourcompany.com)
        </p>
      </div>

      {/* Publish Site Reminder */}
      {!config.customDomain && config.deploymentUrls && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-900 mb-1 text-sm">Important: Publish Your Site First</h4>
              <p className="text-sm text-yellow-800 mb-3">
                Your custom domain will point to your published trust center. Make sure to publish at least once before setting up the custom domain, otherwise visitors will see an error.
              </p>
              <div className="bg-white border border-yellow-300 rounded p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700">Your Deployment URLs:</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Live (production):</span>
                    <span className="font-mono text-gray-900">{config.deploymentUrls.live}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Preview (staging):</span>
                    <span className="font-mono text-gray-900">{config.deploymentUrls.preview}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last (previous):</span>
                    <span className="font-mono text-gray-900">{config.deploymentUrls.last}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                💡 Your CNAME record will point to the <strong>Live</strong> URL by default. Publish your trust center to make it accessible at that URL.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overview Section */}
      {!config.customDomain && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <Info className="h-5 w-5 text-blue-600 mr-2" />
              How Custom Domains Work
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              Instead of sharing a default Netlify URL like <span className="font-mono text-xs bg-white px-2 py-1 rounded">yoursite-123abc.netlify.app</span>,
              you can use your own professional domain like <span className="font-mono text-xs bg-white px-2 py-1 rounded">trust.yourcompany.com</span>.
            </p>
          </div>

          <div className="bg-white border border-blue-300 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-3">Setup Options</h5>
            <div className="space-y-3 text-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-800 rounded-full flex items-center justify-center font-semibold mr-3">1</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Email Domain Match (Instant Setup)</div>
                  <p className="text-gray-700">
                    If your login email domain matches the domain you want to use, verification is automatic.
                  </p>
                  <div className="mt-2 bg-green-50 rounded p-2 text-xs">
                    <strong>Example:</strong> You're logged in as <span className="font-mono">admin@acme.com</span> and want to use <span className="font-mono">trust.acme.com</span> — No DNS verification needed! Just add a CNAME record and you're done.
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-semibold mr-3">2</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Different Domain (TXT Verification)</div>
                  <p className="text-gray-700">
                    If you own a different domain, you'll verify ownership by adding a TXT record.
                  </p>
                  <div className="mt-2 bg-blue-50 rounded p-2 text-xs">
                    <strong>Example:</strong> You're logged in as <span className="font-mono">admin@acme.com</span> but want to use <span className="font-mono">trust.acme-security.com</span> — You'll add a TXT record to prove you own the domain, then add a CNAME record.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-sm text-gray-700">
              <strong>What you'll need:</strong> Access to your domain's DNS settings (usually at your domain registrar like GoDaddy, Cloudflare, Route 53, or managed by your IT department)
            </p>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800 whitespace-pre-line">{error}</div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">{success}</div>
        </div>
      )}

      {/* Current Domain Status */}
      {config.customDomain && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-mono text-sm text-gray-900">{config.customDomain}</span>
                {config.domainVerified ? (
                  <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Pending Verification
                  </span>
                )}
              </div>
              {config.domainVerified && config.verifiedAt && (
                <p className="text-xs text-gray-500">
                  Verified {new Date(config.verifiedAt).toLocaleDateString()} via{' '}
                  {config.verificationMethod === 'sso-match' ? 'SSO email match' : 'DNS TXT record'}
                </p>
              )}
            </div>
            <button
              onClick={handleRemoveDomain}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* DNS Status Monitor */}
      {config.customDomain && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 text-sm">DNS Configuration Status</h4>
            <button
              onClick={checkDnsStatus}
              disabled={checkingDns}
              className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50 flex items-center"
            >
              {checkingDns ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Checking...
                </>
              ) : (
                'Refresh'
              )}
            </button>
          </div>

          {!dnsStatus ? (
            <div className="flex items-center justify-center py-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Checking DNS records...
            </div>
          ) : (
            <div className="space-y-3">
              {/* CNAME Status */}
              <div className="flex items-start">
              {dnsStatus.cname?.configured ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">CNAME Record</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    dnsStatus.cname?.configured
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {dnsStatus.cname?.configured ? 'Configured' : 'Not Found'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  <div><strong>Expected:</strong> <span className="font-mono">{dnsStatus.cname?.expected}</span></div>
                  {dnsStatus.cname?.actual && (
                    <div><strong>Actual:</strong> <span className="font-mono">{dnsStatus.cname.actual}</span></div>
                  )}
                  {!dnsStatus.cname?.configured && !dnsStatus.cname?.actual && (
                    <div className="text-yellow-700 mt-1">Add the CNAME record to your DNS provider</div>
                  )}
                </div>
              </div>
            </div>

            {/* TXT Status (only if verification needed and domain not auto-verified) */}
            {dnsStatus.txt && !config.domainVerified && (
              <div className="flex items-start pt-3 border-t border-gray-200">
                {dnsStatus.txt.configured ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">TXT Record (Verification)</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      dnsStatus.txt.configured
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {dnsStatus.txt.configured ? 'Configured' : 'Not Found'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    <div><strong>Looking for:</strong> <span className="font-mono text-xs break-all">{dnsStatus.txt.expected?.substring(0, 40)}...</span></div>
                    {!dnsStatus.txt.configured && (
                      <div className="text-yellow-700 mt-1">Add the TXT record at _netlify.{config.customDomain}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          )}

          {dnsStatus && (
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
              {dnsStatus.cname?.configured ? (
                '✓ DNS configured correctly'
              ) : (
                <>Auto-checking every 30 seconds • DNS propagation can take 5-60 minutes</>
              )}
            </div>
          )}
        </div>
      )}

      {/* Setup or Update Domain */}
      {(!config.customDomain || !config.domainVerified) && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {config.customDomain ? 'Update Domain' : 'Domain Name'}
            </label>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="trust.yourcompany.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use a subdomain like trust.yourcompany.com or security.yourcompany.com
            </p>
          </div>

          {/* Preview DNS Records - Show immediately when domain is entered */}
          {previewToken && newDomain && !willAutoVerify && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">Get a head start!</h4>
                  <p className="text-xs text-gray-700 mb-3">
                    You can add these DNS records now while you complete the setup. This way DNS will be propagating while you finish configuring.
                  </p>

                  <div className="bg-white border border-blue-300 rounded p-3 space-y-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase">TXT Record (for verification)</div>
                    <div className="grid grid-cols-[80px_1fr_auto] gap-2 text-xs">
                      <div className="font-medium text-gray-700">Name:</div>
                      <div className="font-mono text-gray-900">_netlify.{newDomain}</div>
                      <button
                        onClick={() => copyToClipboard(`_netlify.${newDomain}`, 'preview-name')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'preview-name' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                      <div className="font-medium text-gray-700">Value:</div>
                      <div className="font-mono text-gray-900 break-all">{previewToken}</div>
                      <button
                        onClick={() => copyToClipboard(previewToken, 'preview-token')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'preview-token' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white border border-blue-300 rounded p-3 space-y-2 mt-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase">CNAME Record (for routing)</div>
                    <div className="grid grid-cols-[80px_1fr_auto] gap-2 text-xs">
                      <div className="font-medium text-gray-700">Name:</div>
                      <div className="font-mono text-gray-900">{newDomain.split('.')[0]}</div>
                      <button
                        onClick={() => copyToClipboard(newDomain.split('.')[0], 'preview-cname')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'preview-cname' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                      <div className="font-medium text-gray-700">Value:</div>
                      <div className="font-mono text-gray-900">{currentSiteUrl}</div>
                      <button
                        onClick={() => copyToClipboard(currentSiteUrl, 'preview-value')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'preview-value' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Auto-verify notice */}
          {previewToken && newDomain && willAutoVerify && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1 text-sm">Quick Setup Available!</h4>
                  <p className="text-xs text-green-800 mb-2">
                    Your email domain matches <span className="font-mono">{newDomain}</span>, so you'll only need to add a CNAME record (no TXT verification needed).
                  </p>
                  <div className="bg-white border border-green-300 rounded p-3 space-y-2">
                    <div className="text-xs font-semibold text-gray-500 uppercase">CNAME Record (add this now)</div>
                    <div className="grid grid-cols-[80px_1fr_auto] gap-2 text-xs">
                      <div className="font-medium text-gray-700">Name:</div>
                      <div className="font-mono text-gray-900">{newDomain.split('.')[0]}</div>
                      <button
                        onClick={() => copyToClipboard(newDomain.split('.')[0], 'quick-cname')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'quick-cname' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                      <div className="font-medium text-gray-700">Value:</div>
                      <div className="font-mono text-gray-900">{currentSiteUrl}</div>
                      <button
                        onClick={() => copyToClipboard(currentSiteUrl, 'quick-value')}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {copied === 'quick-value' ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleSetupDomain}
            disabled={saving || !newDomain}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {config.customDomain ? 'Update Domain' : 'Setup Domain'}
          </button>
        </div>
      )}

      {/* DNS Verification Instructions */}
      {config.customDomain && !config.domainVerified && config.verificationMethod === 'dns-txt' && !dnsStatus?.cname?.configured && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-6">
          {/* Overview */}
          <div className="bg-white border border-blue-300 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
              <Info className="h-5 w-5 text-blue-600 mr-2" />
              What You Need to Do
            </h4>
            <p className="text-sm text-gray-700 mb-3">
              To use <strong className="font-mono">{config.customDomain}</strong> for your trust center, you need to add 2 DNS records with whoever manages your domain's DNS (typically GoDaddy, Cloudflare, AWS Route 53, or your IT department).
            </p>
            <div className="bg-blue-50 rounded p-3 text-sm text-gray-700 space-y-1">
              <p><strong>Record 1 (TXT):</strong> Proves you own the domain</p>
              <p><strong>Record 2 (CNAME):</strong> Points your domain to your live trust center</p>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3 text-lg">Step 1: Add DNS Records</h4>
            <p className="text-sm text-gray-700 mb-4">
              Log in to your DNS provider and add these two records. If you're not sure who manages your DNS, contact your IT department or check where you registered <strong>{config.customDomain.split('.').slice(-2).join('.')}</strong>.
            </p>

            {/* Verification TXT Record */}
            <div className="bg-white border border-blue-300 rounded-lg p-3 sm:p-4 mb-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Verification Record</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[280px]">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 w-20 sm:w-auto">Type:</td>
                      <td className="py-2 font-mono">TXT</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Name:</td>
                      <td className="py-2 font-mono">
                        <div className="flex items-start justify-between gap-2">
                          <span className="break-all">_netlify.{config.customDomain}</span>
                          <button
                            onClick={() => copyToClipboard(`_netlify.${config.customDomain}`, 'name')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                          >
                            {copied === 'name' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Value:</td>
                      <td className="py-2 font-mono">
                        <div className="flex items-start justify-between gap-2">
                          <span className="break-all text-xs sm:text-sm">{config.verificationToken}</span>
                          <button
                            onClick={() => copyToClipboard(config.verificationToken || '', 'token')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                          >
                            {copied === 'token' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* CNAME Record for actual routing */}
            <div className="bg-white border border-blue-300 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Routing Record (Points Your Domain to Trust Center)</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[280px]">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 w-24">Type:</td>
                      <td className="py-2 font-mono">CNAME</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Name/Host:</td>
                      <td className="py-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-mono text-sm break-all">{config.customDomain.split('.')[0]}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              (Some providers want just "<strong>{config.customDomain.split('.')[0]}</strong>", others want the full "<strong className="break-all">{config.customDomain}</strong>")
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(config.customDomain?.split('.')[0] || '', 'cname-short')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                            title="Copy subdomain only"
                          >
                            {copied === 'cname-short' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Value/Target:</td>
                      <td className="py-2 font-mono">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="break-all">{currentSiteUrl}</div>
                            <div className="text-xs text-gray-500 mt-1 font-normal">
                              (This points {config.customDomain} → your live trust center)
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(currentSiteUrl, 'value')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                          >
                            {copied === 'value' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-medium text-gray-700">TTL:</td>
                      <td className="py-2 text-xs sm:text-sm text-gray-600">
                        Auto or 3600 (default is fine)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Provider-Specific Quick Guides */}
          <div className="bg-white border border-blue-300 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-3">Quick Guide by Provider</h5>
            <div className="space-y-3 text-sm">
              <div>
                <strong className="text-gray-900">Cloudflare:</strong>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-700 ml-2">
                  <li>Go to DNS tab in your domain dashboard</li>
                  <li>Click "Add record"</li>
                  <li>For TXT record: Type=TXT, Name=_netlify, Content=(token above)</li>
                  <li>For CNAME: Type=CNAME, Name={config.customDomain.split('.')[0]}, Target={currentSiteUrl}</li>
                  <li><strong>Keep orange cloud (proxy) ON</strong> for the CNAME record - this enables SSL/HTTPS</li>
                </ol>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <strong className="text-gray-900">GoDaddy:</strong>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-700 ml-2">
                  <li>Go to DNS Management for your domain</li>
                  <li>Click "Add" to create new records</li>
                  <li>For TXT: Type=TXT, Host=_netlify.{config.customDomain}, Value=(token above)</li>
                  <li>For CNAME: Type=CNAME, Host={config.customDomain.split('.')[0]}, Points to={currentSiteUrl}</li>
                  <li>SSL/HTTPS is automatically provided by the target URL</li>
                </ol>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <strong className="text-gray-900">AWS Route 53:</strong>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-700 ml-2">
                  <li>Select your hosted zone</li>
                  <li>Click "Create record"</li>
                  <li>For TXT: Record name=_netlify.{config.customDomain}, Type=TXT, Value=(token)</li>
                  <li>For CNAME: Record name={config.customDomain.split('.')[0]}, Type=CNAME, Value={currentSiteUrl}</li>
                  <li>SSL/HTTPS is automatically provided by the target URL</li>
                </ol>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <strong className="text-gray-900">Other Providers:</strong>
                <p className="mt-1 text-gray-700 ml-2">
                  Look for "DNS Settings", "DNS Management", or "Nameservers" in your domain control panel. The exact labels vary, but you'll need to add a TXT record and a CNAME record using the values shown above.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3 text-lg">Step 2: Wait for DNS Propagation</h4>
            <div className="bg-white border border-blue-300 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3">
                After adding the DNS records, you need to wait for them to propagate across the internet. This usually takes <strong>5-10 minutes</strong>, but can take up to 48 hours in rare cases.
              </p>
              <div className="bg-gray-50 rounded p-3 text-sm text-gray-700">
                <p className="mb-2"><strong>While you wait, you can check DNS propagation:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Use <a href="https://dnschecker.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dnschecker.org</a> to see if your CNAME record is live</li>
                  <li>It may show different results in different locations - that's normal during propagation</li>
                  <li>Once you see "{currentSiteUrl}" when checking your CNAME, you're ready for Step 3</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-3 text-lg">Step 3: Verify Domain Ownership</h4>
            <div className="bg-white border border-blue-300 rounded-lg p-4 mb-3">
              <p className="text-sm text-gray-700 mb-3">
                Once your DNS records have propagated, click the button below to verify you own the domain. This checks for the TXT record you added in Step 1.
              </p>
              <button
                onClick={handleVerifyDomain}
                disabled={verifying}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold"
              >
                {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {verifying ? 'Checking DNS Records...' : 'Verify Domain Ownership'}
              </button>
              <p className="text-xs text-gray-500 mt-2">
                If verification fails, wait a few more minutes for DNS propagation and try again.
              </p>
            </div>
          </div>

          {/* What Happens After Verification */}
          <div className="bg-green-50 border border-green-300 rounded-lg p-4">
            <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
              <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
              What Happens After Verification?
            </h5>
            <div className="text-sm text-gray-700 space-y-2">
              <p>Once verified, your custom domain is fully active:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Your trust center will be accessible at <strong className="font-mono">{config.customDomain}</strong></li>
                <li>HTTPS/SSL will be automatically enabled (may take 1-2 minutes)</li>
                <li>Your customers can share this professional URL instead of the default Netlify URL</li>
                <li>The domain will automatically point to your latest published content</li>
              </ul>
            </div>
          </div>

          {/* Helpful Links */}
          <div className="pt-4 border-t border-blue-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Need Help?</p>
            <div className="grid grid-cols-2 gap-2">
              <a
                href="https://www.cloudflare.com/learning/dns/dns-records/dns-txt-record/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
              >
                How to add TXT records <ExternalLink className="h-3 w-3 ml-1" />
              </a>
              <a
                href="https://www.cloudflare.com/learning/dns/dns-records/dns-cname-record/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
              >
                How to add CNAME records <ExternalLink className="h-3 w-3 ml-1" />
              </a>
              <a
                href="https://dnschecker.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
              >
                Check DNS propagation <ExternalLink className="h-3 w-3 ml-1" />
              </a>
              <a
                href="https://mxtoolbox.com/SuperTool.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
              >
                DNS lookup tool <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps - When DNS is configured */}
      {config.customDomain && dnsStatus?.cname?.configured && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-green-900 mb-1">DNS Configured Successfully!</h4>
                <p className="text-sm text-green-800">
                  Your custom domain <span className="font-mono">{config.customDomain}</span> is pointing to the correct location.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h5 className="font-semibold text-gray-900 mb-2">Next Step: Publish Your Trust Center</h5>
                <p className="text-sm text-gray-700 mb-3">
                  Your DNS is configured correctly, but visitors won't see anything until you publish your trust center.
                </p>
                <button
                  onClick={() => {
                    if (onNavigateToDeployments) {
                      onNavigateToDeployments()
                      // Scroll to top after navigation
                      setTimeout(() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }, 100)
                    } else {
                      window.location.href = '/admin#trust-center-deployments'
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  Go to Publishing Settings →
                </button>
                <p className="text-xs text-gray-600 mt-3">
                  Once published, your trust center will be live at <span className="font-mono">{config.customDomain}</span> with HTTPS automatically enabled.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SSO Verification Success - Still need to add CNAME */}
      {config.customDomain && config.domainVerified && config.verificationMethod === 'sso-match' && !dnsStatus?.cname?.configured && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-green-900 mb-1">Domain Verified Automatically ✓</h4>
                <p className="text-sm text-green-800">
                  Your domain was verified automatically because your login email domain matches your trust center's configured domain. No TXT record needed!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">Final Step: Add CNAME Record</h4>
              <p className="text-sm text-gray-700 mb-4">
                To make <strong className="font-mono">{config.customDomain}</strong> work, you just need to add one CNAME record with your DNS provider:
              </p>
            </div>

            <div className="bg-white border border-blue-300 rounded-lg p-3 sm:p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[280px]">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 w-24 sm:w-32">Type:</td>
                      <td className="py-2 font-mono">CNAME</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Name/Host:</td>
                      <td className="py-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-mono break-all">{config.customDomain.split('.')[0]}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              (Just the subdomain: <strong>{config.customDomain.split('.')[0]}</strong>)
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(config.customDomain?.split('.')[0] || '', 'sso-cname')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                          >
                            {copied === 'sso-cname' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-medium text-gray-700 align-top">Value/Target:</td>
                      <td className="py-2 font-mono">
                        <div className="flex items-start justify-between gap-2">
                          <span className="break-all">{currentSiteUrl}</span>
                          <button
                            onClick={() => copyToClipboard(currentSiteUrl, 'sso-value')}
                            className="p-2 hover:bg-gray-100 rounded flex-shrink-0 touch-manipulation"
                          >
                            {copied === 'sso-value' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-blue-300 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2">Where to add this:</h5>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 ml-2">
                <li>Log in to your DNS provider (Cloudflare, GoDaddy, Route 53, etc.)</li>
                <li>Find DNS settings for <strong>{config.customDomain.split('.').slice(-2).join('.')}</strong></li>
                <li>Click "Add Record" or similar</li>
                <li>Set Type to CNAME, Name to <strong>{config.customDomain.split('.')[0]}</strong>, and Value to <strong>{currentSiteUrl}</strong></li>
                <li>Save the record</li>
                <li>Wait 5-10 minutes for DNS to propagate</li>
                <li>Visit <strong className="font-mono">{config.customDomain}</strong> to see your trust center!</li>
              </ol>
            </div>

            <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">
              <strong>Note:</strong> HTTPS/SSL will be automatically enabled within 1-2 minutes after DNS propagates.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomDomainSettings
